import { yupResolver } from '@hookform/resolvers/yup';
import { Grid, MenuItem, Typography } from '@mui/material';
import Chip from '@mui/material/Chip';
import { useCallback, useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import AppButton from 'src/components/appComponents/AppButton';
import AppLoadingAndErrorWrapper from 'src/components/appComponents/AppLoadingAndErrorWrapper';
import FormBlock from 'src/components/appComponents/FormBlock';
import FormProvider, {
  RHFAutocomplete,
  RHFCheckbox,
  RHFRadioGroup,
  RHFSelect,
  RHFTextField,
} from 'src/components/hook-form';
import { useSnackbar } from 'src/components/snackbar';
import {
  useGetAllChannelsQuery,
  useGetCategoriesContentsQuery,
  useGetCategoriesQuery,
  usePublishChannelParentSuggestionMutation,
} from 'src/graphql';
import { useParams, useRouter } from 'src/routes/hooks';
import { paths } from 'src/routes/paths';
import { IPublishChannelParentSuggestion } from 'src/types/parentSuggestion';
import PublishChannelTablePage from './PublishChannelTable';
import { PublishChannelParentSuggestioSchema } from 'src/schema';

export default function PublishChannelParentSuggestionForm({
  suggestionVideo,
  isLoadingGetSuggestionVideo,
}: any) {
  const router = useRouter();
  const { suggestedId } = useParams();
  const { enqueueSnackbar } = useSnackbar();
  const [selectedRow, setSelectedRow] = useState<string[]>([]);

  const methods = useForm<IPublishChannelParentSuggestion>({
    resolver: yupResolver(PublishChannelParentSuggestioSchema),
  });

  const { data: channels, isLoading: isLoadingChannels } = useGetAllChannelsQuery();
  const { data: categories, isLoading: isLoadingCategories } = useGetCategoriesQuery();
  const { data: categoriesContent, isLoading: isLoadingCategoriesContent } =
    useGetCategoriesContentsQuery();

  const getCategoryContentsById = (id: string) => {
    const category = categoriesContent?.categories.find((category: any) => category?.id === id);
    const categoryContent = category ? category.categories_contents : null;
    return categoryContent?.map((item) => ({
      id: item.content.id,
      name: item.content.name,
    }));
  };

  const { mutate: publishVideo, isLoading: isLoadingPublishVideo } =
    usePublishChannelParentSuggestionMutation({
      onSuccess: () => {
        enqueueSnackbar({ variant: 'success', message: 'Completed Successfully' });
        router.push(paths.dashboard.parentSuggestions.suggestionDetails(suggestedId ?? ''));
      },
    });

  const onSubmit = useCallback(
    async (data: IPublishChannelParentSuggestion) => {
      const channelRelatedContents: any = data?.contentIds?.map((item: any) => ({
        contentId: item?.id,
      }));
      delete data.contentIds;
      const { createNewChannel, ...restData } = data;

      const input =
        methods.watch('createNewChannel') == 'addToExist'
          ? {
              suggestedId,
              channelId: data?.channelId?.id,
              videoIds: selectedRow,
            }
          : {
              ...restData,
              suggestedId,
              minTargetAge: parseInt(data?.minTargetAge?.split('-')[0]!),
              maxTargetAge: parseInt(data?.minTargetAge?.split('-')[1]!),
              channels_contents: { data: channelRelatedContents },
              videoIds: selectedRow,
            };

      publishVideo({
        input: {
          ...input,
        },
      });
    },
    [selectedRow]
  );

  // For set default values
  useEffect(() => {
    if (!isLoadingGetSuggestionVideo && suggestionVideo?.app_suggested_by_pk) {
      const defaultValues: any = {
        name: {
          en: suggestionVideo?.app_suggested_by_pk?.title?.en,
          ar: suggestionVideo?.app_suggested_by_pk?.title?.ar,
        },
        description: {
          en: suggestionVideo?.app_suggested_by_pk?.description?.en,
          ar: suggestionVideo?.app_suggested_by_pk?.description?.ar,
        },
        createNewChannel: 'addToExist',
      };
      methods.reset(defaultValues);
    }
  }, [suggestionVideo, methods]);

  return (
    <AppLoadingAndErrorWrapper
      isLoading={isLoadingGetSuggestionVideo || isLoadingCategoriesContent}
      sx={{ height: 'auto' }}
    >
      <FormProvider methods={methods} onSubmit={methods.handleSubmit(onSubmit)}>
        <Grid container rowSpacing={3} alignItems="center">
          <FormBlock
            title=""
            inputField={
              <RHFRadioGroup
                row
                spacing={3}
                name="createNewChannel"
                defaultValue="addToExist"
                options={[
                  {
                    id: 'addToExist',
                    name: {
                      en: 'Add to the existing channel',
                    },
                  },
                  {
                    id: 'createNew',
                    name: {
                      en: 'Create new Channel',
                    },
                  },
                ]}
              />
            }
          />

          {methods.watch('createNewChannel') == 'addToExist' ? (
            <FormBlock
              title="Channel Name*"
              inputField={
                <RHFAutocomplete
                  name="channelId"
                  autoHighlight
                  loading={isLoadingChannels}
                  options={channels?.channels ?? []}
                  getOptionLabel={(option: any) => option?.name?.ar}
                  renderOption={(props, option) => (
                    <li {...props} key={option?.id}>
                      {option?.name?.ar}
                    </li>
                  )}
                />
              }
            />
          ) : (
            <>
              <FormBlock
                title="English Channel Name*"
                inputField={<RHFTextField name="name.en" label="Enter english name" />}
              />
              <FormBlock
                title="Arabic Channel Name*"
                inputField={<RHFTextField name="name.ar" label="Enter arabic name" />}
              />
              <FormBlock
                title="English Description*"
                inputField={
                  <RHFTextField name="description.en" label="Enter english short description" />
                }
              />
              <FormBlock
                title="Arabic Description*"
                inputField={
                  <RHFTextField name="description.ar" label="Enter arabic short description" />
                }
              />
              <FormBlock
                title="Related Category*"
                inputField={
                  <RHFRadioGroup
                    row
                    spacing={3}
                    name="categoryId"
                    options={categories?.categories ?? []}
                  />
                }
              />
              <FormBlock
                title="Related Content*"
                inputField={
                  <RHFAutocomplete
                    name="contentIds"
                    label="Choose content"
                    multiple
                    disableCloseOnSelect
                    disabled={!methods.watch()?.categoryId}
                    options={getCategoryContentsById(methods.watch()?.categoryId!) ?? []}
                    isOptionEqualToValue={(option, value) => option?.id === value?.id}
                    renderOption={(props, option: any) => (
                      <li {...props} key={option?.id}>
                        {option?.name?.en}
                      </li>
                    )}
                    renderTags={(selected, getTagProps) =>
                      selected.map((option, index) => (
                        <Chip
                          {...getTagProps({ index })}
                          key={option?.id}
                          label={option?.name?.en}
                          size="small"
                          color="primary"
                          variant="filled"
                        />
                      ))
                    }
                  />
                }
              />
              <FormBlock
                title="Target age group*"
                inputField={
                  <RHFSelect name="minTargetAge" label="Choose ages">
                    <MenuItem value="4-6">4-6</MenuItem>
                    <MenuItem value="7-10">7-10</MenuItem>
                    <MenuItem value="11-13">11-13</MenuItem>
                    <MenuItem value="14-16">Above</MenuItem>
                  </RHFSelect>
                }
              />
            </>
          )}

          <Grid item xs={12} mb={4}>
            <Typography variant="h4">Parent Suggestion Video</Typography>
          </Grid>
          <PublishChannelTablePage selected={selectedRow} setSelected={setSelectedRow} />

          <Grid item container justifyContent="flex-end">
            <AppButton
              label="Publish"
              type="submit"
              color="primary"
              isLoading={isLoadingPublishVideo}
            />
          </Grid>
        </Grid>
      </FormProvider>
    </AppLoadingAndErrorWrapper>
  );
}
